import { useEffect, useCallback, useRef } from 'react';
import {
  doc,
  collection,
  onSnapshot,
  query,
  orderBy,
  limit,
  writeBatch,
  updateDoc,
  getDocs,
  getDoc,
  where,
  Timestamp
} from 'firebase/firestore';
import { ref, onValue } from 'firebase/database';
import { db, rtdb } from '../lib/firebase';
import { Room, Player, Round, Question, GameItem } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useGame } from '../contexts/GameContext';
import { usePresence } from '../contexts/PresenceContext';
import { useToast } from '../contexts/ToastContext';
import { resolvePlaceholders, isTemplateQuestion } from '../utils/templateUtils';
import { useNavigate } from 'react-router-dom';
import { useRoomReconnection } from './useRoomReconnection';
import { revealRoundAtomic, transferHostAtomic } from '../utils/roomOperations';

// How long a host may be offline (RTDB) before the deterministic successor
// promotes itself. Long enough to survive a refresh / brief network blip so
// that refreshing does NOT cost the host their room.
const HOST_TRANSFER_GRACE_MS = 15000;

export const useGameState = (roomCode: string | null) => {
  const { user } = useAuth();
  const { room, players, dispatch } = useGame();
  const { showError, showInfo, showSuccess } = useToast();
  const { setPresence } = usePresence();
  const navigate = useNavigate();
  const { clearRoomState } = useRoomReconnection();

  // Refs used inside subscription callbacks to avoid stale closures AND to keep
  // effects from re-subscribing in a loop (the previous version listed state it
  // set itself in the dependency array, tearing down/re-creating the listener).
  const roomExistedRef = useRef(false);
  const initialLoadCompleteRef = useRef(false);
  const playersRef = useRef<Player[]>(players);
  const prevHostIdRef = useRef<string | null>(null);

  useEffect(() => {
    playersRef.current = players;
  }, [players]);

  // Re-establish RTDB presence whenever we are on a room page with a user.
  // This is what makes a hard refresh / direct navigation to /game/:code (or
  // any room route) show the player as online again, not just create/join.
  useEffect(() => {
    if (!roomCode || !user?.uid) return;
    setPresence(roomCode).catch((err) => console.error('presence setup failed', err));
  }, [roomCode, user?.uid, setPresence]);

  // Subscribe to room data (waits for auth so the read isn't permission-denied
  // on a hard refresh of a room route).
  useEffect(() => {
    if (!roomCode || !user?.uid) {
      roomExistedRef.current = false;
      initialLoadCompleteRef.current = false;
      return;
    }

    const roomRef = doc(db, 'rooms', roomCode);
    const unsubscribe = onSnapshot(
      roomRef,
      (snap) => {
        if (snap.exists()) {
          const roomData = snap.data();
          const nextRoom = { code: roomCode, ...roomData } as Room;

          // Room archived by host (explicit close of the last player / summary).
          if (roomData.status === 'ARCHIVED' && roomData.archived === true) {
            const isCurrentUserHost = user?.uid && roomData.hostId === user.uid;
            if (!isCurrentUserHost && user?.uid) {
              clearRoomState();
              dispatch({ type: 'RESET_GAME' });
              navigate('/');
              return;
            }
          }

          dispatch({ type: 'SET_ROOM', payload: nextRoom });
          roomExistedRef.current = true;
          initialLoadCompleteRef.current = true;
        } else {
          dispatch({ type: 'SET_ROOM', payload: null });
          if (roomExistedRef.current && initialLoadCompleteRef.current) {
            showError('La sala ha sido eliminada por el anfitrión');
          }
          initialLoadCompleteRef.current = true;
        }
      },
      (error) => {
        console.error('Error listening to room:', error);
        showError('Error al conectar con la sala');
      }
    );

    return unsubscribe;
  }, [roomCode, dispatch, showError, user?.uid, clearRoomState, navigate]);

  // Subscribe to players data
  useEffect(() => {
    if (!roomCode || !user?.uid) return;

    const playersQuery = query(
      collection(db, 'rooms', roomCode, 'players'),
      orderBy('joinedAt')
    );

    const unsubscribe = onSnapshot(
      playersQuery,
      (snapshot) => {
        const nextPlayers = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data()
        })) as Player[];
        dispatch({ type: 'SET_PLAYERS', payload: nextPlayers });
      },
      (error) => {
        console.error('Error listening to players:', error);
        showError('Error al obtener la lista de jugadores');
      }
    );

    return unsubscribe;
  }, [roomCode, dispatch, showError, user?.uid]);

  // Subscribe to current round data (highest index)
  useEffect(() => {
    if (!roomCode || !user?.uid) return;

    const currentRoundQuery = query(
      collection(db, 'rooms', roomCode, 'rounds'),
      orderBy('index', 'desc'),
      limit(1)
    );

    const unsubscribe = onSnapshot(
      currentRoundQuery,
      (snapshot) => {
        if (!snapshot.empty) {
          const roundDoc = snapshot.docs[0];
          dispatch({
            type: 'SET_CURRENT_ROUND',
            payload: { id: roundDoc.id, ...roundDoc.data() } as Round
          });
        } else {
          dispatch({ type: 'SET_CURRENT_ROUND', payload: null });
        }
      },
      (error) => {
        console.error('Error listening to current round:', error);
        showError('Error al obtener la ronda actual');
      }
    );

    return unsubscribe;
  }, [roomCode, dispatch, showError]);

  // Subscribe to presence in RTDB — the single source of truth for `connected`.
  useEffect(() => {
    if (!roomCode || !user?.uid) return;

    const presenceRef = ref(rtdb, `status/rooms/${roomCode}/players`);
    const unsubscribe = onValue(
      presenceRef,
      (snapshot) => {
        const presenceData = snapshot.val() || {};
        const map: Record<string, boolean> = {};
        Object.entries(presenceData).forEach(([playerId, presence]: [string, any]) => {
          if (presence && typeof presence === 'object') {
            map[playerId] = presence.state === 'online';
          }
        });
        dispatch({ type: 'SET_PRESENCE', payload: map });
      },
      (error) => {
        console.error('Error listening to presence:', error);
      }
    );

    return unsubscribe;
  }, [roomCode, dispatch, user?.uid]);

  // Notify everyone when the host changes (transfer).
  useEffect(() => {
    const hostId = room?.hostId ?? null;
    const prev = prevHostIdRef.current;
    if (hostId && prev && hostId !== prev) {
      if (hostId === user?.uid) {
        showSuccess('Ahora eres el anfitrión de la sala');
      } else {
        const name = players.find((p) => p.id === hostId)?.nickname;
        showInfo(name ? `Nuevo anfitrión: ${name}` : 'Se ha asignado un nuevo anfitrión');
      }
    }
    prevHostIdRef.current = hostId;
  }, [room?.hostId, user?.uid, players, showInfo, showSuccess]);

  // Host-transfer watchdog. Non-host clients monitor the host's RTDB presence;
  // if the host stays offline past the grace period, the deterministic
  // successor (earliest-joined, still-connected non-host) promotes itself via an
  // idempotent transaction. This replaces the old behaviour of destroying the
  // room when the host disconnected.
  useEffect(() => {
    if (!roomCode || !room?.hostId || !user?.uid) return;
    const hostId = room.hostId;
    if (hostId === user.uid) return; // I'm the host, nothing to watch

    const hostPresenceRef = ref(rtdb, `status/rooms/${roomCode}/players/${hostId}`);
    let graceTimer: ReturnType<typeof setTimeout> | null = null;

    const clearTimer = () => {
      if (graceTimer) {
        clearTimeout(graceTimer);
        graceTimer = null;
      }
    };

    const unsubscribe = onValue(hostPresenceRef, (snap) => {
      const presence = snap.val();
      const hostOnline = presence?.state === 'online';

      if (hostOnline) {
        clearTimer();
        return;
      }
      if (graceTimer) return; // already counting down

      graceTimer = setTimeout(async () => {
        graceTimer = null;
        // Determine the deterministic successor among currently-connected,
        // non-host players (earliest joinedAt wins).
        const connectedOthers = playersRef.current
          .filter((p) => p.id !== hostId && p.connected)
          .sort((a, b) => {
            const at = a.joinedAt?.toMillis?.() ?? 0;
            const bt = b.joinedAt?.toMillis?.() ?? 0;
            return at - bt;
          });
        const successor = connectedOthers[0];
        if (!successor || successor.id !== user.uid) return; // not my turn

        try {
          await transferHostAtomic(db, roomCode, hostId, user.uid);
        } catch (error) {
          console.error('Host transfer failed:', error);
        }
      }, HOST_TRANSFER_GRACE_MS);
    });

    return () => {
      unsubscribe();
      clearTimer();
    };
  }, [roomCode, room?.hostId, user?.uid]);

  // Idempotent COLLECTING -> REVEAL transition (used by the deadline timer and
  // the host's "Siguiente" button for questions). Safe under concurrency.
  const revealRound = useCallback(
    async (roundId: string): Promise<void> => {
      if (!roomCode) return;
      try {
        await revealRoundAtomic(db, roomCode, roundId);
      } catch (error: any) {
        console.error('Error revealing round:', error);
      }
    },
    [roomCode]
  );

  // Advance to the next round (host-driven from the results screen).
  const nextRound = useCallback(async (): Promise<void> => {
    if (!roomCode || !user?.uid) return;

    try {
      const roomRef = doc(db, 'rooms', roomCode);
      const roomSnap = await getDoc(roomRef);
      const roomData = roomSnap.data();
      if (!roomData) return;

      const gameItemIds: string[] = roomData.gameQuestions || [];
      const numRounds: number = roomData.settings?.numRounds || 10;
      // Never run past the pre-generated, persisted sequence: the game ends
      // instead of getting stuck if content runs short.
      const totalRounds = Math.min(numRounds, gameItemIds.length || numRounds);

      const currentRoundIndex: number = roomData.currentRoundIndex ?? 0;
      const nextRoundIndex = currentRoundIndex + 1;

      if (nextRoundIndex >= totalRounds) {
        await updateDoc(roomRef, {
          status: 'ENDED',
          currentRoundIndex: nextRoundIndex
        });
        return;
      }

      // Guard against double-advance (e.g. rapid double click): if a round for
      // nextRoundIndex already exists, do nothing.
      const roundsRef = collection(db, 'rooms', roomCode, 'rounds');
      const existing = await getDocs(
        query(roundsRef, orderBy('index', 'desc'), limit(1))
      );
      const maxExistingIndex = existing.empty
        ? -1
        : (existing.docs[0].data().index as number);
      if (maxExistingIndex >= nextRoundIndex) {
        return;
      }

      const playersSnap = await getDocs(collection(db, 'rooms', roomCode, 'players'));
      const roundPlayers = playersSnap.docs.map((d) => ({
        id: d.id,
        ...d.data()
      })) as Player[];

      const nextItemId = gameItemIds[nextRoundIndex];
      if (!nextItemId) {
        throw new Error('No hay más contenido disponible para esta ronda.');
      }

      const isCurrentChallenge = nextItemId.startsWith('c_');
      const sourceCollection = isCurrentChallenge ? 'challenges' : 'questions';
      const itemQuery = query(
        collection(db, sourceCollection),
        where('id', '==', nextItemId),
        limit(1)
      );
      let itemSnap = await getDocs(itemQuery);
      if (itemSnap.empty) {
        // One retry to tolerate eventual consistency of the content collection.
        await new Promise((resolve) => setTimeout(resolve, 800));
        itemSnap = await getDocs(itemQuery);
        if (itemSnap.empty) {
          throw new Error(`Contenido ${nextItemId} no encontrado en la base de datos.`);
        }
      }

      const nextItem = {
        id: itemSnap.docs[0].id,
        ...itemSnap.docs[0].data()
      } as GameItem;

      let textResolved = nextItem.template || '';
      let placeholdersResolved: Record<string, string> = {};
      let templateId: string | undefined;

      if (
        isTemplateQuestion(nextItem as Question) &&
        nextItem.template &&
        nextItem.placeholders
      ) {
        const resolution = resolvePlaceholders(
          nextItem.template,
          nextItem.placeholders,
          roundPlayers
        );
        textResolved = resolution.resolvedText;
        placeholdersResolved = resolution.placeholdersResolved;
        templateId = nextItem.id;
      }

      const batch = writeBatch(db);
      batch.update(roomRef, { currentRoundIndex: nextRoundIndex });

      const newRoundRef = doc(roundsRef);
      const roundData: Record<string, any> = {
        index: nextRoundIndex,
        questionId: nextItem.id,
        questionText: nextItem.template,
        questionTextResolved: textResolved,
        category: nextItem.category,
        status: isCurrentChallenge ? 'CHALLENGE_DISPLAY' : 'COLLECTING',
        startedAt: Timestamp.now(),
        isChallenge: isCurrentChallenge,
        tally: {},
        winners: []
      };

      if (!isCurrentChallenge) {
        const roundSeconds = roomData.settings?.roundSeconds || 15;
        roundData.deadlineAt = Timestamp.fromMillis(Date.now() + roundSeconds * 1000);
      }

      if (templateId) {
        roundData.questionTemplateId = templateId;
        roundData.placeholdersResolved = placeholdersResolved;
      }

      batch.set(newRoundRef, roundData);
      await batch.commit();
    } catch (error: any) {
      console.error('Error advancing to next round:', error);
      showError(error.message || 'Error al avanzar a la siguiente ronda');
      throw error;
    }
  }, [roomCode, user, showError]);

  // Finish a round without vote computation (used for challenge rounds).
  const finishRound = useCallback(
    async (roundId: string): Promise<void> => {
      if (!roomCode) return;
      try {
        const roundRef = doc(db, 'rooms', roomCode, 'rounds', roundId);
        await updateDoc(roundRef, { status: 'REVEAL' });
      } catch (error: any) {
        console.error('Error finishing round:', error);
        showError(error.message || 'Error al finalizar la ronda');
        throw error;
      }
    },
    [roomCode, showError]
  );

  return {
    nextRound,
    finishRound,
    revealRound
  };
};
